import {
  reviewRechargePoints,
  STREAK_DAILY_RECHARGE_POINTS,
} from "@-/dev.improve.haniverse.lib.cards.review";
import {
  differenceInCalendarDays,
  format,
  milliseconds,
  parse,
  startOfDay,
  subDays,
} from "@-/com.npmjs.date-fns";
import {
  COVERAGE_TIMELINE_POINTS,
  DAILY_REVIEW_WINDOW_DAYS,
  HSK_BAR_COLORS,
  RETENTION_WEEKS,
  type HskLevelProgress,
  type LearnedGraphemeRow,
  type LearnedTimelinePoint,
  type LearningStatsData,
  type ProgressStageKey,
} from "@-/dev.improve.haniverse.lib.learning-stats.entities";
import { pickHskLevel, type HskVersionKey } from "@-/dev.improve.haniverse.lib.morpheme.grade.hsk";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import arrayLength from "@-/com.npmjs.extra-array__length";

export function buildLearnedTimeline(
  learned: readonly LearnedGraphemeRow[],
): LearnedTimelinePoint[] {
  if (hasLength(learned, 0)) {
    return [];
  }
  // SUBTLEX-CH coverage is a per-million quantity → multiply by 100 / 1_000_000.
  const perMillionToPercent = (perMillion: number) => (perMillion / 1_000_000) * 100;

  const dayBuckets = new Map<string, { count: number; perMillion: number; sortKey: number }>();
  for (const row of learned) {
    const dayStart = startOfDay(new Date(row.startedStudyingAt));
    const key = format(dayStart, "yyyy-MM-dd");
    const bucket = dayBuckets.get(key) ?? {
      count: 0,
      perMillion: 0,
      sortKey: dayStart.getTime(),
    };
    bucket.count += 1;
    bucket.perMillion += row.frequencyPerMillion ?? 0;
    dayBuckets.set(key, bucket);
  }
  const sortedDays = [...dayBuckets.entries()].sort((a, b) => a[1].sortKey - b[1].sortKey);
  let runningCount = 0;
  let runningPerMillion = 0;
  const out: LearnedTimelinePoint[] = [];
  for (const [dayKey, bucket] of sortedDays) {
    runningCount += bucket.count;
    runningPerMillion += bucket.perMillion;
    out.push({
      coveragePercent: Math.min(
        100,
        Math.round(perMillionToPercent(runningPerMillion) * 100) / 100,
      ),
      dayKey,
      learnedCount: runningCount,
    });
  }
  return out;
}

export function buildHskProgress(
  learned: readonly LearnedGraphemeRow[],
  totalsByLevel: ReadonlyMap<number, number>,
  hskVersion: HskVersionKey,
): LearningStatsData["hskProgress"] {
  return buildHskLevelProgress(
    learned.map((row) => ({ level: pickHskLevel(row, hskVersion), stage: row.stage })),
    totalsByLevel,
  );
}

/**
 * One row per level in `totalsByLevel`, ascending, with the learned items in that level split by
 * stage. Items without a level (outside the version's lists) are not counted anywhere.
 */
export function buildHskLevelProgress(
  learned: Iterable<{ level: number | null; stage: ProgressStageKey }>,
  totalsByLevel: ReadonlyMap<number, number>,
): HskLevelProgress[] {
  const learnedByLevel = new Map<number, Record<ProgressStageKey, number>>();
  for (const { level, stage } of learned) {
    if (level === null) {
      continue;
    }
    const stages = learnedByLevel.get(level) ?? { familiar: 0, mastered: 0, new: 0, proficient: 0 };
    stages[stage] += 1;
    learnedByLevel.set(level, stages);
  }
  const levels = [...totalsByLevel.keys()].sort((a, b) => a - b);
  return levels.map((level, index) => {
    const total = totalsByLevel.get(level) ?? 0;
    const countsByStage = learnedByLevel.get(level) ?? {
      familiar: 0,
      mastered: 0,
      new: 0,
      proficient: 0,
    };
    const learnedCount = Object.values(countsByStage).reduce((sum, count) => sum + count, 0);
    const percent = total === 0 ? 0 : Math.round((learnedCount / total) * 100);
    return {
      color: HSK_BAR_COLORS[index % arrayLength(HSK_BAR_COLORS)] ?? "#2563EB",
      countsByStage,
      learned: learnedCount,
      level,
      percent,
      total,
    };
  });
}

export function buildDailyReviewSeries(
  reviewedAtList: readonly string[],
  todayStart: Date,
): LearningStatsData["dailyReviewSeries"] {
  const buckets = new Map<string, number>();
  for (const iso of reviewedAtList) {
    const key = format(new Date(iso), "yyyy-MM-dd");
    buckets.set(key, (buckets.get(key) ?? 0) + 1);
  }
  const out: LearningStatsData["dailyReviewSeries"] = [];
  for (let i = DAILY_REVIEW_WINDOW_DAYS - 1; i >= 0; i--) {
    const day = subDays(todayStart, i);
    const key = format(day, "yyyy-MM-dd");
    const value = buckets.get(key) ?? 0;
    out.push({
      frontColor: i === 0 ? "#2563EB" : "#93C5FD",
      label: format(day, "EEE"),
      value,
    });
  }
  return out;
}

export function buildCoverageTimeline(
  learned: readonly LearnedGraphemeRow[],
  todayStart: Date,
): { lifetimePercent: number; series: LearningStatsData["coverageTimeline"] } {
  const sorted = [...learned]
    .filter((row) => row.frequencyPerMillion !== null && row.frequencyPerMillion !== undefined)
    .sort(
      (a, b) => new Date(a.startedStudyingAt).getTime() - new Date(b.startedStudyingAt).getTime(),
    );

  // SUBTLEX-CH frequencies are normalized to occurrences per million tokens, so a
  // character's share of the corpus is `frequencyPerMillion / 1_000_000`. Summing
  // those fractions over learned characters (× 100 for a percentage) gives the share
  // of everyday written Chinese they cover — an additive, 0–100 bounded quantity.
  const perMillionToPercent = (perMillion: number) => (perMillion / 1_000_000) * 100;

  let runningPerMillion = 0;
  type CoveragePoint = { coverage: number; date: Date };
  const events: CoveragePoint[] = [];
  for (const row of sorted) {
    runningPerMillion += row.frequencyPerMillion ?? 0;
    events.push({
      coverage: perMillionToPercent(runningPerMillion),
      date: new Date(row.startedStudyingAt),
    });
  }

  const lifetimePercent = Math.min(100, perMillionToPercent(runningPerMillion));

  if (hasLength(events, 0)) {
    return { lifetimePercent: 0, series: [] };
  }

  const firstEvent = events[0];
  if (firstEvent === undefined) {
    return { lifetimePercent: 0, series: [] };
  }
  const firstEventTime = firstEvent.date.getTime();
  const lastEventTime = todayStart.getTime();
  const span = Math.max(milliseconds({ days: 1 }), lastEventTime - firstEventTime);
  const stepMs = span / (COVERAGE_TIMELINE_POINTS - 1);

  const series: LearningStatsData["coverageTimeline"] = [];
  let cursor = 0;
  for (let i = 0; i < COVERAGE_TIMELINE_POINTS; i++) {
    const sampleTime = firstEventTime + stepMs * i;
    while (cursor < arrayLength(events) - 1) {
      const nextEvent = events[cursor + 1];
      if (nextEvent === undefined || nextEvent.date.getTime() > sampleTime) {
        break;
      }
      cursor++;
    }
    const eventAtCursor = events[cursor];
    if (eventAtCursor === undefined) {
      continue;
    }
    const coverage = eventAtCursor.date.getTime() <= sampleTime ? eventAtCursor.coverage : 0;
    series.push({
      dateLabel: format(new Date(sampleTime), "MMM d"),
      value: Math.min(100, Math.round(coverage * 10) / 10),
    });
  }
  return { lifetimePercent, series };
}

export function buildRetentionSeries(
  reviewLog: readonly { rating: number; review: string }[],
  todayStart: Date,
): { last30dPercent: number | null; series: LearningStatsData["retentionSeries"] } {
  const buckets: { correct: number; endDate: Date; reviews: number; startDate: Date }[] = [];
  for (let i = RETENTION_WEEKS - 1; i >= 0; i--) {
    const endDate = subDays(todayStart, i * 7);
    const startDate = subDays(endDate, 7);
    buckets.push({ correct: 0, endDate, reviews: 0, startDate });
  }
  for (const entry of reviewLog) {
    const reviewTime = new Date(entry.review).getTime();
    for (const bucket of buckets) {
      if (reviewTime >= bucket.startDate.getTime() && reviewTime < bucket.endDate.getTime()) {
        bucket.reviews += 1;
        if (entry.rating >= 3) {
          bucket.correct += 1;
        }
        break;
      }
    }
  }
  const series = buckets.map((bucket, index) => {
    const value = bucket.reviews === 0 ? 0 : Math.round((bucket.correct / bucket.reviews) * 100);
    return {
      label: `W${index + 1}`,
      reviews: bucket.reviews,
      value,
    };
  });

  const thirtyDaysAgo = subDays(todayStart, 30).getTime();
  let recent30Total = 0;
  let recent30Correct = 0;
  for (const entry of reviewLog) {
    if (new Date(entry.review).getTime() >= thirtyDaysAgo) {
      recent30Total += 1;
      if (entry.rating >= 3) {
        recent30Correct += 1;
      }
    }
  }
  const last30dPercent =
    recent30Total === 0 ? null : Math.round((recent30Correct / recent30Total) * 100);

  return { last30dPercent, series };
}

/** Adds one review's review recharge points to its local day's total. */
export function addDailyRechargePoints(
  dailyPoints: Map<string, number>,
  { rating, reviewedAt }: { rating: number; reviewedAt: string },
): void {
  const dayKey = format(new Date(reviewedAt), "yyyy-MM-dd");
  dailyPoints.set(dayKey, (dailyPoints.get(dayKey) ?? 0) + reviewRechargePoints(rating));
}

/**
 * A streak day is a local day whose reviews earned {@link STREAK_DAILY_RECHARGE_POINTS} review
 * recharge points. The current streak ends today, or yesterday while today is not yet complete.
 */
export function computeStreaks(
  dailyPoints: ReadonlyMap<string, number>,
  todayStart: Date,
): { current: number; longest: number } {
  const dayKeySet = new Set<string>();
  for (const [dayKey, points] of dailyPoints) {
    if (points >= STREAK_DAILY_RECHARGE_POINTS) {
      dayKeySet.add(dayKey);
    }
  }
  if (dayKeySet.size === 0) {
    return { current: 0, longest: 0 };
  }

  let current = 0;
  const todayKey = format(todayStart, "yyyy-MM-dd");
  const hasToday = dayKeySet.has(todayKey);
  const streakAnchor = hasToday ? todayStart : subDays(todayStart, 1);
  if (hasToday || dayKeySet.has(format(streakAnchor, "yyyy-MM-dd"))) {
    let cursor = streakAnchor;
    while (dayKeySet.has(format(cursor, "yyyy-MM-dd"))) {
      current += 1;
      cursor = subDays(cursor, 1);
    }
  }

  const sortedKeyList = [...dayKeySet].sort();
  let longest = 0;
  let run = 0;
  let prev: Date | null = null;
  for (const key of sortedKeyList) {
    const date = parse(key, "yyyy-MM-dd", new Date());
    if (prev !== null && differenceInCalendarDays(date, prev) === 1) {
      run += 1;
    } else {
      run = 1;
    }
    if (run > longest) {
      longest = run;
    }
    prev = date;
  }

  return { current, longest };
}
